import React, {
    ReactElement, useEffect, useMemo, useState,
} from "react";
import { Form } from "react-bootstrap";
import useKeyboardShortcuts from "../../../contexts/KeyboardShortcutContext";
import useSelectionState, { Ticket } from "../../../pages/agent/selection-state";
import { NoDataOrError } from "../../../pages/error/Error";
import {
    ChatStatus,
    CustomFieldValueInput, FieldTypedValue, FormFieldReplacementInput, RequiredField, Ticket as RawTicket,
    TicketInput, useCloseSettingsQuery, useEndChatMutation, useGetChatQuery, useSaveTicketMutation,
} from "../../../__generated__/api";
import Confirm from "../../confirm/Confirm";
import FormDisplay, { TAGS_ID } from "../../form/Form";
import Loading from "../../loading/Loading";
import Select, { SelectInstanceHandler } from "../../select/Select";

export default function EndChat(): ReactElement {
    const selectionState = useSelectionState();
    const [selectedTickets, setSelectedTickets] = useState<Ticket[]>([]);

    const { data: chatValues } = useGetChatQuery({
        skip: !selectedTickets.firstElement?.id,
        variables: {
            id: selectedTickets.firstElement?.id,
        },
    });

    const [saveTicket] = useSaveTicketMutation();

    const { data, loading, error } = useCloseSettingsQuery();
    const [sendEndChat] = useEndChatMutation();

    const [shouldShow, setShouldShow] = useState<boolean>(false);
    const [message, setMessage] = useState<string>("");
    const [tags, setTags] = useState<string[]>([]);
    const [ticketInput, setTicketInput] = useState<TicketInput>();

    const [selectedTicketIds, setSelectedTicketIds] = useState<string[]>([]);
    const selectRef = React.createRef<SelectInstanceHandler>();

    const requiredFields = useMemo(() => new Set(data?.settings?.customization?.requiredCloseFields ?? []), [data]);

    useEffect(() => {
        setTags(chatValues?.chat?.ticket?.tags ?? []);
    }, [chatValues?.chat?.ticket?.tags]);

    const startEndChat = async (tickets?: Ticket[]) => {
        setShouldShow(true);
        selectRef.current?.focus();

        const selected = tickets ?? selectionState
            .getSelection()
            .filter(ticket => ticket.status === ChatStatus.Assigned
                || ticket.status === ChatStatus.OnHold
                || ticket.status === ChatStatus.Snoozed);
        setSelectedTickets(selected);
        setSelectedTicketIds(selected.toIdList("id"));
    };

    selectionState.registerEndChatHandler((tickets?: Ticket[]) => {
        startEndChat(tickets);
    });

    useEffect(() => {
        if (!shouldShow) {
            setTags([]);
        } else {
            setTags(chatValues?.chat?.ticket?.tags ?? []);
        }
        setMessage("");

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShow]);

    const endChat = async () => {
        if (ticketInput) {
            await saveTicket({
                variables: {
                    id: selectedTickets.firstElement.ticket?.id,
                    ticket: ticketInput,
                },
            });
        }

        // selectionState.delete(...selectedTicketIds);
        setShouldShow(false);
        selectionState.closeCurrent();
        selectionState.setChatStatuses(selectedTicketIds, ChatStatus.Finished);
        await sendEndChat({
            variables: {
                chatEndMessages: selectedTicketIds.map(chatId => ({
                    chatId,
                    message,
                    tags,
                })),
            },
        });
    };

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "k+e",
            context: "default",
            description: "End the conversation",
            group: "Conversations",
            handler: () => startEndChat(),
        });
    });

    if (!data || error) {
        return <NoDataOrError error={error} />;
    }

    const handleKeyDown = (key: React.KeyboardEvent<HTMLElement>) => {
        if (key.key === "Escape") {
            selectRef.current?.blur();
        }
    };

    const handleChange = (ticket: TicketInput) => {
        const copy = { ...ticketInput, ...ticket, customField: undefined };
        copy.customFields = (ticketInput?.customFields ?? []).concat(
            ticket.customFields as CustomFieldValueInput[],
            [ticket.customField as CustomFieldValueInput],
        ).filterNull().dedupe(row => row.formFieldId, (_left, right) => right);
        setTicketInput(copy);
    };

    return (
        <Confirm
            confirmText="End Chat"
            shouldShow={shouldShow}
            closeText="Close"
            onHide={() => setShouldShow(false)}
            onConfirm={() => endChat()}
        >
            {selectedTickets.isEmpty || loading ? (
                <div className="overflow-auto w-100 h-100">
                    <div className={`centered-container-lg m-auto w-100 h-100 flex-stretch-0 `}>
                        <Loading />
                    </div>
                </div>
            )
                : (
                    <div className="flex-grow-1 p-3 d-flex flex-column">
                        <h5>
                            End Chat
                        </h5>
                        <FormDisplay
                            isFormField
                            ticket={chatValues?.chat?.ticket as RawTicket}
                            isEditable
                            isEditing
                            onChange={handleChange}
                            shouldFilterAgentRequired
                            descriptionMessage={(
                                <small className="text-danger mb-3 d-block">
                                    The following fields are required to be filled out to close this ticket.
                                </small>
                            )}
                            className="border-bottom rounded mt-2 border border-danger rounded px-3 py-2"
                            skipFields={[
                                TAGS_ID,
                            ]}
                        />

                        <Form.Label className="text-primary mt-4">
                            Tags
                            {" "}
                            {!requiredFields.has(RequiredField.Tags) && <small>(optional)</small>}
                        </Form.Label>
                        <Select
                            ref={selectRef}
                            placeholder="Tags"
                            isMulti
                            minRequired={requiredFields.has(RequiredField.Tags) ? 1 : 0}
                            options={data.settings?.customization?.tags?.map(tag => ({
                                label: tag,
                                value: tag,
                                data: "",
                            })) ?? []}
                            onKeyDown={handleKeyDown}
                            onChange={selected => setTags(selected.map(row => row.value))}
                            value={tags.map(tag => ({
                                label: tag,
                                value: tag,
                            }))}
                        />

                        <Form.Text>
                            What tags would you like to associate?
                        </Form.Text>

                        <Form.Label className="text-primary mt-4">
                            Ticket Notes
                            {" "}
                            {!requiredFields.has(RequiredField.TicketNotes) && <small>(optional)</small>}
                        </Form.Label>

                        <Form.Control
                            as="textarea"
                            type="text"
                            required={requiredFields.has(RequiredField.TicketNotes)}
                            placeholder="Resolved this ticket by..."
                            className="default-text-height p-2"
                            onChange={evt => setMessage(evt.target.value)}
                        />
                        <Form.Text>
                            Any additional comments you want to add to the ticket before closing it.
                            This will not be visible to the customer.
                        </Form.Text>

                    </div>
                )}
        </Confirm>
    );
}

EndChat.defaultProps = {
    refreshData: () => {
        // nothing
    },
    onChange: (value: FieldTypedValue, replacements?: FormFieldReplacementInput[]) => {
        // nothing
    },
};
